/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 * ⚠️ IAs: NÃO reescrever | APENAS search_replace | PRESERVAR estrutura
 */
import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import CompanySwitchModal from './CompanySwitchModal';
import PendingApprovalsModal from './PendingApprovalsModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Briefcase, 
  Users, 
  MessageSquare, 
  FileText,
  Settings,
  LogOut,
  User,
  Bell,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Leaf,
  Search,
  BarChart3,
  UserCheck,
  ClipboardCheck,
  Building2,
  FileSearch,
  FilePlus,
  Brain,
  Target,
  TrendingDown,
  ThermometerSun,
  ShieldCheck,
  Shield,
  GraduationCap,
  RefreshCw,
  LayoutGrid,
  Megaphone,
  CalendarDays as CalendarDaysIcon,
} from 'lucide-react';

export const DashboardLayout = ({ children, menuItems }) => {
  const { 
    user, 
    logout, 
    managedCompanies, 
    selectedCompany, 
    fetchManagedCompanies, 
    selectCompany,
    companySwitchModal,
    confirmCompanySwitch,
    cancelCompanySwitch
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [expandedSections, setExpandedSections] = React.useState({
    'Recrutamento': false,
    'Gestão de Pessoas': false,
    'Diagnósticos': false,
    'Testes Comportamentais': false,
    'Relatórios': false,
    'Configurações': false
  });

  // Group menu items by section - must be declared before useEffect that uses it
  const groupedMenuItems = React.useMemo(() => {
    const groups = [];
    let currentGroup = null;
    
    menuItems.forEach((item, index) => {
      if (item.isSection) {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = { label: item.label, items: [] };
      } else if (currentGroup) {
        currentGroup.items.push(item);
      } else {
        // Items before first section (like Dashboard)
        groups.push({ label: null, items: [item] });
      }
    });
    
    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [menuItems]);

  // Auto-expand section containing active route on mount and route change
  React.useEffect(() => {
    const activeSection = groupedMenuItems.find(group => 
      group.label && group.items?.some(item => 
        location.pathname === item.path || location.pathname.startsWith(item.path + '/')
      )
    );
    
    if (activeSection?.label) {
      setExpandedSections(prev => {
        // Close all sections and open only the active one
        const newState = {};
        Object.keys(prev).forEach(key => {
          newState[key] = key === activeSection.label;
        });
        return newState;
      });
    }
  }, [location.pathname, groupedMenuItems]);

  // Fetch managed companies when component mounts (for COMPANY users)
  React.useEffect(() => {
    if (user?.role === 'COMPANY') {
      fetchManagedCompanies();
    }
  }, [user, fetchManagedCompanies]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Accordion behavior: close all other sections when opening one
  const toggleSection = (label) => {
    setExpandedSections(prev => {
      const isCurrentlyExpanded = prev[label];
      
      // If closing the current section, just close it
      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [label]: false
        };
      }
      
      // If opening a new section, close all others (accordion behavior)
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = key === label;
      });
      return newState;
    });
  };

  // Check if any item in section is active
  const isSectionActive = (items) => {
    return items.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
  };

  // Section colors for visual distinction
  const SECTION_COLORS = {
    'Recrutamento': {
      active: 'text-blue-700 bg-blue-50/70',
      hover: 'hover:text-blue-600 hover:bg-blue-50/50',
      dot: 'bg-blue-500',
      border: 'border-blue-200',
      itemActive: 'from-blue-100 to-blue-50 text-blue-900',
      itemHover: 'from-blue-50 via-slate-50 to-transparent'
    },
    'Gestão de Pessoas': {
      active: 'text-emerald-700 bg-emerald-50/70',
      hover: 'hover:text-emerald-600 hover:bg-emerald-50/50',
      dot: 'bg-emerald-500',
      border: 'border-emerald-200',
      itemActive: 'from-emerald-100 to-emerald-50 text-emerald-900',
      itemHover: 'from-emerald-50 via-slate-50 to-transparent'
    },
    'Diagnósticos': {
      active: 'text-amber-700 bg-amber-50/70',
      hover: 'hover:text-amber-600 hover:bg-amber-50/50',
      dot: 'bg-amber-500',
      border: 'border-amber-200',
      itemActive: 'from-amber-100 to-amber-50 text-amber-900',
      itemHover: 'from-amber-50 via-slate-50 to-transparent'
    },
    'Testes Comportamentais': {
      active: 'text-violet-700 bg-violet-50/70',
      hover: 'hover:text-violet-600 hover:bg-violet-50/50',
      dot: 'bg-violet-500',
      border: 'border-violet-200',
      itemActive: 'from-violet-100 to-violet-50 text-violet-900',
      itemHover: 'from-violet-50 via-slate-50 to-transparent'
    },
    'Relatórios': {
      active: 'text-cyan-700 bg-cyan-50/70',
      hover: 'hover:text-cyan-600 hover:bg-cyan-50/50',
      dot: 'bg-cyan-500',
      border: 'border-cyan-200',
      itemActive: 'from-cyan-100 to-cyan-50 text-cyan-900',
      itemHover: 'from-cyan-50 via-slate-50 to-transparent'
    },
    'Configurações': {
      active: 'text-slate-700 bg-slate-100/70',
      hover: 'hover:text-slate-600 hover:bg-slate-50/50',
      dot: 'bg-slate-500',
      border: 'border-slate-200',
      itemActive: 'from-slate-100 to-slate-50 text-slate-900',
      itemHover: 'from-slate-50 via-slate-50 to-transparent'
    }
  };

  // Get section color or default
  const getSectionColor = (label) => {
    return SECTION_COLORS[label] || {
      active: 'text-slate-700 bg-slate-100/50',
      hover: 'hover:text-slate-600 hover:bg-slate-50',
      dot: 'bg-cyan-500',
      border: 'border-slate-200',
      itemActive: 'from-slate-100 to-slate-50 text-slate-900',
      itemHover: 'from-cyan-50 via-slate-50 to-transparent'
    };
  };

  // Render a single menu item with section-specific colors
  const renderMenuItem = (item, isSubItem = false, sectionLabel = null) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    const colors = getSectionColor(sectionLabel);
    
    if (isSubItem) {
      return (
        <NavLink
          key={item.path}
          to={item.path}
          className={`group relative flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-all duration-200 ease-out border-l-2 -ml-[1px] overflow-hidden ${
            isActive 
              ? `text-slate-900 ${colors.border} bg-gradient-to-r ${colors.itemActive}` 
              : `text-slate-500 border-transparent hover:text-slate-900 hover:${colors.border}`
          }`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {/* Hover stripe effect */}
          <span className={`absolute inset-0 bg-gradient-to-r ${colors.itemHover} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out ${isActive ? 'translate-x-0' : ''}`} />
          <item.icon className="w-4 h-4 relative z-10 transition-transform duration-200 group-hover:scale-110" strokeWidth={2} />
          <span className="tracking-tight relative z-10">{item.label}</span>
        </NavLink>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={`group relative flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium rounded-lg transition-all duration-200 ease-out overflow-hidden ${
          isActive 
            ? `bg-gradient-to-r ${colors.itemActive} shadow-sm` 
            : 'text-slate-500 hover:text-slate-900'
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        {/* Hover stripe effect */}
        <span className={`absolute inset-0 bg-gradient-to-r ${colors.itemHover} rounded-lg transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out ${isActive ? 'hidden' : ''}`} />
        <item.icon className="w-4 h-4 relative z-10 transition-transform duration-200 group-hover:scale-110" strokeWidth={2} />
        <span className="tracking-tight relative z-10">{item.label}</span>
      </NavLink>
    );
  };

  // Render section with items
  const renderSection = (group, index) => {
    if (!group.label) {
      // Items without section (like Dashboard)
      return (
        <div key={`nosection-${index}`} className="mb-1">
          {group.items.map(item => renderMenuItem(item))}
        </div>
      );
    }

    const isExpanded = expandedSections[group.label];
    const hasActiveItem = isSectionActive(group.items);
    const colors = getSectionColor(group.label);

    return (
      <div key={group.label} className="mb-2">
        <button
          onClick={() => toggleSection(group.label)}
          className={`group w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-wider transition-all duration-200 rounded-lg ${
            hasActiveItem 
              ? colors.active 
              : `text-slate-400 ${colors.hover}`
          }`}
          data-testid={`section-${group.label.toLowerCase().replace(/\s/g, '-')}`}
        >
          <div className="flex items-center gap-2">
            {/* Active indicator dot */}
            {hasActiveItem && !isExpanded && (
              <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} animate-pulse`} />
            )}
            <span className="transition-transform duration-200 group-hover:translate-x-0.5">{group.label}</span>
          </div>
          <ChevronDown 
            className={`w-3.5 h-3.5 transition-all duration-300 ease-out ${
              isExpanded ? 'rotate-0' : '-rotate-90'
            } ${hasActiveItem ? colors.dot.replace('bg-', 'text-') : ''}`} 
          />
        </button>
        
        {/* Animated expand/collapse container */}
        <div 
          className={`ml-3 border-l flex flex-col gap-0.5 overflow-hidden transition-all duration-300 ease-out ${
            hasActiveItem ? colors.border : 'border-slate-200'
          } ${
            isExpanded 
              ? 'max-h-[500px] opacity-100 mt-1 mb-2 py-1' 
              : 'max-h-0 opacity-0 mt-0 mb-0 py-0'
          }`}
        >
          {group.items.map((item, idx) => (
            <div 
              key={item.path}
              className="transition-all duration-300 ease-out"
              style={{ 
                transitionDelay: isExpanded ? `${idx * 50}ms` : '0ms',
                transform: isExpanded ? 'translateX(0)' : 'translateX(-10px)',
                opacity: isExpanded ? 1 : 0
              }}
            >
              {renderMenuItem(item, true, group.label)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Modern Swiss Design */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-60 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-14 px-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">G2</span>
              </div>
              <span className="font-semibold text-[15px] text-slate-900 tracking-tight">G2eGente</span>
            </div>
            <button 
              className="lg:hidden text-slate-400 hover:text-slate-600 p-1 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-3 overflow-y-auto">
            {groupedMenuItems.map((group, index) => renderSection(group, index))}
          </nav>

          {/* User info & Logout */}
          <div className="p-3 border-t border-slate-100">
            <div className="flex items-center gap-2.5 px-2 py-2 mb-2 rounded-lg transition-colors duration-200 hover:bg-slate-50">
              <div className="w-8 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-105">
                <User className="w-4 h-4 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">
                  {user?.email}
                </p>
                <p className="text-[10px] text-slate-400">
                  {user?.role === 'COMPANY' ? 'Empresa' : user?.role === 'CANDIDATE' ? 'Candidato' : 'Admin'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="group relative w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] font-medium rounded-lg transition-all duration-200 text-slate-500 hover:text-red-600 text-left overflow-hidden"
              data-testid="sidebar-logout"
            >
              {/* Hover stripe effect */}
              <span className="absolute inset-0 bg-gradient-to-r from-red-50 via-red-50/50 to-transparent rounded-lg transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out" />
              <LogOut className="w-4 h-4 relative z-10 transition-transform duration-200 group-hover:scale-110 group-hover:-translate-x-0.5" />
              <span className="relative z-10">Sair</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-60">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-full px-4 sm:px-6">
            <button
              className="lg:hidden text-slate-600 hover:text-slate-900"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-4 ml-auto">
              {/* Company Selector for Recruiters */}
              {user?.role === 'COMPANY' && managedCompanies.length > 0 && (
                <>
                  {/* Botão de voltar quando empresa gerenciada está selecionada */}
                  {selectedCompany && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => selectCompany(null)}
                      className="hidden sm:flex items-center gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="return-to-main-company"
                    >
                      <X className="w-4 h-4" />
                      <span className="text-xs">Voltar</span>
                    </Button>
                  )}
                  <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant={selectedCompany ? "default" : "outline"}
                      className={`flex items-center gap-2 max-w-[200px] ${
                        selectedCompany ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : ''
                      }`}
                      data-testid="company-selector-trigger"
                    >
                      <Building2 className={`w-4 h-4 flex-shrink-0 ${selectedCompany ? 'text-white' : 'text-cyan-600'}`} />
                      <span className="truncate text-sm">
                        {selectedCompany?.nome || 'Minha Empresa'}
                      </span>
                      <ChevronDown className={`w-4 h-4 flex-shrink-0 ${selectedCompany ? 'text-white' : 'text-slate-500'}`} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Trocar Empresa
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    {/* Opção de voltar para empresa principal */}
                    <DropdownMenuItem
                      onClick={() => selectCompany(null)}
                      className={`flex items-center gap-2 cursor-pointer ${
                        !selectedCompany ? 'bg-slate-100' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">Minha Empresa Principal</p>
                        <p className="text-xs text-slate-500">Conta original</p>
                      </div>
                      {!selectedCompany && (
                        <UserCheck className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                      )}
                    </DropdownMenuItem>
                    
                    {/* Opção de visão consolidada */}
                    <DropdownMenuItem
                      onClick={() => {
                        selectCompany({ id: 'consolidated', nome: 'Visão Geral', is_consolidated: true });
                        navigate('/company/consolidated');
                      }}
                      className={`flex items-center gap-2 cursor-pointer ${
                        selectedCompany?.is_consolidated ? 'bg-green-50' : ''
                      }`}
                      data-testid="consolidated-view-option"
                    >
                      <LayoutGrid className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-700">Visão Geral</p>
                        <p className="text-xs text-slate-500">Todas as empresas</p>
                      </div>
                      {selectedCompany?.is_consolidated && (
                        <UserCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                      )}
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
                      Empresas Gerenciadas
                    </DropdownMenuLabel>
                    
                    {managedCompanies.map((company) => (
                      <DropdownMenuItem
                        key={company.cnpj}
                        onClick={() => selectCompany(company)}
                        className={`flex items-center gap-2 cursor-pointer ${
                          selectedCompany?.cnpj === company.cnpj ? 'bg-cyan-50 text-cyan-700' : ''
                        }`}
                        data-testid={`company-option-${company.cnpj}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{company.nome}</p>
                          <p className="text-xs text-slate-500">
                            {company.cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}
                          </p>
                        </div>
                        {selectedCompany?.cnpj === company.cnpj && (
                          <UserCheck className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                        )}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate('/company/settings#empresas-gerenciadas')}
                      className="flex items-center gap-2 text-slate-600"
                      data-testid="manage-companies-btn"
                    >
                      <Settings className="w-4 h-4" />
                      Gerenciar Empresas
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </>
              )}

              <Button variant="ghost" size="icon" className="relative">
                <Bell className="w-5 h-5 text-slate-600" />
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-600" />
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/')}>
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Página Inicial
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
      
      {/* Modal de troca de empresa */}
      <CompanySwitchModal
        isOpen={companySwitchModal?.isOpen}
        targetCompany={companySwitchModal?.targetCompany}
        currentCompany={selectedCompany}
        onConfirm={confirmCompanySwitch}
        onCancel={cancelCompanySwitch}
      />
      
      {/* Modal de notificação de solicitações pendentes */}
      <PendingApprovalsModal user={user} />
    </div>
  );
};

// Company menu items - Reorganizado conforme solicitação do usuário
export const companyMenuItems = [
  { path: '/company', icon: LayoutDashboard, label: 'Dashboard' },
  
  // RECRUTAMENTO Section
  { isSection: true, label: 'Recrutamento' },
  { path: '/company/job-positions', icon: FilePlus, label: 'Engenharia de Cargo' },
  { path: '/company/jobs', icon: Briefcase, label: 'Minhas Vagas' },
  { path: '/company/candidates', icon: FileSearch, label: 'Selecionar Candidatos' },
  { path: '/company/agenda-entrevistas', icon: CalendarDaysIcon, label: 'Agenda de Entrevistas' },
  
  // GESTÃO DE PESSOAS Section (Avaliação + PDI combinados)
  { isSection: true, label: 'Gestão de Pessoas' },
  { path: '/company/employees', icon: UserCheck, label: 'Funcionários' },
  { path: '/company/treinamentos', icon: GraduationCap, label: 'Treinamentos' },
  // { path: '/company/grades-treinamento', icon: LayoutGrid, label: 'Grades de Treinamento' }, // Oculto temporariamente
  { path: '/company/gestao-atividades', icon: RefreshCw, label: 'Gestão de Atividades' },
  // { path: '/company/matriz-polivalencia', icon: BarChart3, label: 'Matriz de Polivalência' }, // Oculto temporariamente
  { path: '/company/dashboard-gaps', icon: TrendingDown, label: 'Dashboard de Gaps' },
  { path: '/company/performance', icon: ClipboardCheck, label: 'Avaliação de Desempenho' },
  { path: '/company/pdi', icon: Target, label: 'PDI / PDC' },
  
  // DIAGNÓSTICOS Section (Clima + NR-01 + Denúncias combinados)
  { isSection: true, label: 'Diagnósticos' },
  { path: '/company/climate-surveys', icon: ThermometerSun, label: 'Pesquisa de Clima' },
  { path: '/company/nr01', icon: ShieldCheck, label: 'Diagnóstico NR-01' },
  { path: '/company/portal-denuncias', icon: Shield, label: 'Portal de Denúncias' },
  { path: '/company/feed-comunicados', icon: Megaphone, label: 'Feed de Comunicados' },
  
  // TESTES COMPORTAMENTAIS Section (Banco de questões agora dentro)
  { isSection: true, label: 'Testes Comportamentais' },
  { path: '/company/testes-comportamentais', icon: Brain, label: 'Aplicar Testes' },
  { path: '/company/grupos-testes', icon: Users, label: 'Grupos de Testes' },
  { path: '/company/behavioral-questions', icon: MessageSquare, label: 'Banco de Questões' },
  
  // RELATÓRIOS Section
  { isSection: true, label: 'Relatórios' },
  { path: '/company/reports', icon: FileText, label: 'Relatórios' },
  { path: '/company/comparison', icon: BarChart3, label: 'Relatório Comparativo' },
  { path: '/company/esg', icon: Leaf, label: 'Relatório ESG' },
  
  // CONFIG
  { isSection: true, label: 'Configurações' },
  { path: '/company/financeiro', icon: FileText, label: 'Financeiro' },
  { path: '/company/settings', icon: Settings, label: 'Configurações' },
];

// Candidate menu items
export const candidateMenuItems = [
  { path: '/candidate', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/candidate/profile', icon: User, label: 'Meu Perfil' },
  { path: '/candidate/jobs', icon: Briefcase, label: 'Vagas' },
  { path: '/candidate/applications', icon: FileText, label: 'Candidaturas' },
  { path: '/candidate/solicitations', icon: Users, label: 'Solicitações' },
  { path: '/candidate/chats', icon: MessageSquare, label: 'Mensagens' },
  { path: '/candidate/notifications', icon: Bell, label: 'Notificações' },
  { path: '/candidate/settings', icon: Settings, label: 'Configurações' },
];

// Admin menu items
export const adminMenuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/users', icon: Users, label: 'Usuários' },
];
